
import { type FilterOptions } from '@resolute/schema';
import { type SelectOption } from '@resolute/ui';
import { Building2, Home, Map as MapIcon, TreePalm, type LucideIcon } from 'lucide-react';
import { useMemo } from 'react';

import { useFilterOptions } from '@/features/search/api';

/**
 * Configuration-driven hero search. Each category is its own optimized search
 * experience — a tailored set of filter fields — rendered dynamically from this
 * config rather than one hardcoded form per category.
 *
 * `param` is the URL query key a field writes to on submit. `budget` fields
 * encode their value as `"<min>-<max>"` (either side may be blank) and are
 * decoded to priceMin/priceMax at submit time. Fields flagged `captured: true`
 * are surfaced for the user's intent but have no server-side filter yet (the
 * `/search` schema ignores unknown params); when the backend gains support,
 * only this file + the search schema change.
 *
 * Every vocabulary field (type / city / BHK / possession status) is populated
 * from `GET /filter-options`, the same source the results filter panel uses —
 * so an option can never drift from what the inventory actually contains. The
 * lists that were previously hand-written here were measurably wrong: 5 of the
 * 10 type values (`bungalow`, `office`, `shop`, `showroom`, `warehouse`) and
 * ALL FOUR city values matched nothing upstream, so those dropdowns filtered
 * to an empty result set.
 */

export type FieldControl = 'select' | 'budget' | 'toggle';

export interface CatField {
  /** Unique state key within a category. */
  key: string;
  control: FieldControl;
  /** Visible label (also the aria-label for the inline control). */
  label: string;
  /** URL param this field writes (budget decodes to priceMin/priceMax). */
  param?: string;
  options?: SelectOption[];
  /** Override the leading "Any …" option label for selects. */
  anyLabel?: string;
  /** True when there is no server-side filter for this field yet. */
  captured?: boolean;
}

export interface CatConfig {
  id: string;
  label: string;
  Icon: LucideIcon;
  /** Params always applied for this category (scoping). */
  baseParams: Record<string, string>;
  fields: CatField[];
}

/** Drops `order` — the API already returns each list in display order. */
const toOptions = (list: FilterOptions['propertyTypes'] | undefined): SelectOption[] =>
  (list ?? []).map((o) => ({ value: o.value, label: o.label }));

/**
 * Presets with no upstream source. CPzone exposes no price-band or area-band
 * vocabulary (`/filter-options` returns `priceBands: []`), so these stay
 * declared here — they're bucketings of a continuous numeric filter, not a
 * taxonomy that can drift from the inventory.
 */
const AREA_PRESETS: SelectOption[] = [
  { value: '500', label: '500+ sq.ft' },
  { value: '1000', label: '1,000+ sq.ft' },
  { value: '1500', label: '1,500+ sq.ft' },
  { value: '2000', label: '2,000+ sq.ft' },
  { value: '3000', label: '3,000+ sq.ft' },
];
const PLOT_SIZE_PRESETS: SelectOption[] = [
  { value: '1000', label: '1,000+ sq.ft' },
  { value: '2000', label: '2,000+ sq.ft' },
  { value: '4356', label: '0.1+ acre' },
  { value: '21780', label: '0.5+ acre' },
  { value: '43560', label: '1+ acre' },
];

/** Budget presets encoded as "<min>-<max>" (blank side = open-ended). */
const BUDGET: CatField = {
  key: 'price',
  control: 'budget',
  label: 'Budget',
  anyLabel: 'Any budget',
  options: [
    { value: '-5000000', label: 'Under ₹50 L' },
    { value: '5000000-10000000', label: '₹50 L – ₹1 Cr' },
    { value: '10000000-30000000', label: '₹1 – 3 Cr' },
    { value: '30000000-50000000', label: '₹3 – 5 Cr' },
    { value: '50000000-', label: '₹5 Cr+' },
  ],
};

/**
 * Fields with no server-side filter at all — kept so the form still captures
 * the user's intent, and flagged `captured` so `hero-search` knows not to put
 * them in the URL. Left hand-written deliberately: there is nothing upstream
 * to source them from until the backend supports the filter.
 */
const FURNISHING: CatField = {
  key: 'furnishing',
  control: 'select',
  label: 'Furnishing',
  param: 'furnishing',
  options: [
    { value: 'unfurnished', label: 'Unfurnished' },
    { value: 'semi-furnished', label: 'Semi-furnished' },
    { value: 'furnished', label: 'Furnished' },
  ],
  captured: true,
};
const PLOT_TYPE: CatField = {
  key: 'plotType',
  control: 'select',
  label: 'Plot type',
  param: 'plotType',
  options: [
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'agricultural', label: 'Agricultural' },
  ],
  anyLabel: 'Any plot type',
  captured: true,
};

/**
 * The category strip itself — editorial segments (how the business talks about
 * its inventory), not an API vocabulary. `baseParams` scopes each one using
 * values that DO exist upstream.
 *
 * These scope the FILTER BAR only. The free-text search bar below it is a
 * universal search across the whole inventory and deliberately ignores them —
 * see `hero-search.tsx`.
 */
export const CATEGORY_TABS: Omit<CatConfig, 'fields'>[] = [
  {
    id: 'residential',
    label: 'Residential',
    Icon: Home,
    baseParams: { category: 'residential' },
  },
  {
    id: 'commercial',
    label: 'Commercial',
    Icon: Building2,
    baseParams: { category: 'commercial' },
  },
  {
    id: 'weekend',
    label: 'Weekend Homes',
    Icon: TreePalm,
    // `bungalow` used to be scoped in here too; it is not a CPzone type, so it
    // only ever narrowed the query to nothing extra.
    baseParams: { type: 'villa' },
  },
  {
    id: 'land',
    label: 'Land & Plot',
    Icon: MapIcon,
    baseParams: { type: 'plot' },
  },
];

function fieldsFor(id: string, options?: FilterOptions): CatField[] {
  const types = toOptions(options?.propertyTypes);
  const cities = toOptions(options?.cities);
  const configs = toOptions(options?.bhkConfigs);
  const statuses = toOptions(options?.listingStatuses);

  const TYPE = (label = 'Property type', anyLabel?: string): CatField => ({
    key: 'type',
    control: 'select',
    label,
    param: 'type',
    options: types,
    ...(anyLabel ? { anyLabel } : {}),
  });

  switch (id) {
    case 'residential':
      return [
        TYPE('Property type', 'Any type'),
        { key: 'config', control: 'select', label: 'BHK', param: 'config', options: configs, anyLabel: 'Any BHK' },
        {
          key: 'listingStatus',
          control: 'select',
          label: 'Possession',
          param: 'listingStatus',
          options: statuses,
          anyLabel: 'Any status',
        },
        BUDGET,
      ];
    case 'commercial':
      return [
        TYPE('Commercial type', 'Any type'),
        { key: 'areaMin', control: 'select', label: 'Carpet area', param: 'areaMin', options: AREA_PRESETS, anyLabel: 'Any area' },
        FURNISHING,
        BUDGET,
      ];
    case 'weekend':
      return [
        { key: 'city', control: 'select', label: 'Destination', param: 'city', options: cities, anyLabel: 'Anywhere' },
        TYPE(),
        BUDGET,
      ];
    case 'land':
      return [
        { key: 'areaMin', control: 'select', label: 'Plot size', param: 'areaMin', options: PLOT_SIZE_PRESETS },
        PLOT_TYPE,
        BUDGET,
      ];
    default:
      return [BUDGET];
  }
}

/** Full per-category config with every vocabulary list resolved from the API. */
function buildCategoryConfig(options?: FilterOptions): CatConfig[] {
  return CATEGORY_TABS.map((tab) => ({ ...tab, fields: fieldsFor(tab.id, options) }));
}

/**
 * Hero-search config backed by live filter options. Shares the
 * `['filter-options']` query with the header menu and results filter panel, so
 * this adds no request.
 */
export function useCategoryConfig(): CatConfig[] {
  const { data } = useFilterOptions();
  return useMemo(() => buildCategoryConfig(data), [data]);
}

export const DEFAULT_CATEGORY_ID = 'residential';
